/* eslint-disable react/jsx-no-useless-fragment */
import React, { useEffect, useMemo, useState } from 'react';

import { number, object, string } from 'yup';

import { FormSection } from 'src/app/components/lib/form/form';
import { Input, SelectAdornment } from 'src/app/components/lib/input';
import { TimeSelect } from 'src/app/components/lib/time-select';
import {
  convertMinutesToUnit,
  convertUnitToMinutes,
  EVENT_TYPE_TIME_UNIT_OPTIONS,
} from 'src/app/pages/marketing/campaigns/marketing-campaign-builder/helpers';
import { SummarySidebarSection } from 'src/app/pages/marketing/workflows/workflow-campaign-builder/summary-sidebar/styles';
import { enforceWholeNumber } from 'src/app/pages/settings/devices/registers/utils';

import { useWorkflowBuilderContext } from '../../wizard/builder-context';
import { validateWithSchema } from '../../wizard/helpers';

import type { TimingNodeData } from '../../slides/workflow-editor/models';

export const timeDelaySchema = object({
  config: object({
    durationInMinutes: number()
      .when('timeOfDayToSend', {
        is: (timeOfDayToSend: string) => timeOfDayToSend === null,
        then: number().required().moreThan(0),
        otherwise: number().nullable().notRequired(),
      })
      .label('Duration'),
    timeUnitId: number().nullable().required().label('Time unit'),
    timeOfDayToSend: string().nullable().notRequired().label('Time of day to resume'),
  }),
});

export function TimeDelaySection({ nodeData, selectedNodeId }: { nodeData: TimingNodeData; selectedNodeId: string }) {
  const [builderState, dispatch] = useWorkflowBuilderContext();
  const [displayValue, setDisplayValue] = useState('');
  const { selectedNodeIsNew } = builderState;

  // Declare hooks at the top level, before any early returns
  useEffect(() => {
    const timeUnitId = nodeData.config?.timeUnitId || null;
    const durationInMinutes = nodeData.config?.durationInMinutes;

    if (durationInMinutes !== null && timeUnitId !== null) {
      const converted = convertMinutesToUnit(durationInMinutes, timeUnitId);
      setDisplayValue(converted.toString());
    } else {
      setDisplayValue('');
    }
  }, [nodeData]);

  const validationErrors = useMemo(
    () => validateWithSchema(timeDelaySchema, nodeData, selectedNodeIsNew),
    [nodeData, selectedNodeIsNew]
  );

  const timeUnitId = nodeData.config?.timeUnitId || null;
  const timeOfDayToSend = nodeData.config?.timeOfDayToSend || null;

  const setTimeUnitId = (unitId: number | null) => {
    dispatch({
      type: 'set-node-data',
      payload: {
        nodeId: selectedNodeId,
        value: {
          config: {
            ...nodeData.config,
            timeUnitId: unitId,
          },
        },
      },
    });
  };

  const setDuration = (value: string | null) => {
    dispatch({
      type: 'set-node-data',
      payload: {
        nodeId: selectedNodeId,
        value: {
          config: {
            ...nodeData.config,
            durationInMinutes: value ? Number(value) : 0,
          },
        },
      },
    });
  };

  const setTimeOfDayToSend = (time: string | null) => {
    dispatch({
      type: 'set-node-data',
      payload: {
        nodeId: selectedNodeId,
        value: {
          config: {
            ...nodeData.config,
            timeOfDayToSend: time,
          },
        },
      },
    });
  };

  // Available time unit options
  const timeUnitOptions = EVENT_TYPE_TIME_UNIT_OPTIONS.default;

  // Handle time unit change
  const handleUnitChange = (event: any) => {
    const unitId = Number(event.target ? event.target.value : event);
    setTimeUnitId(unitId);
  };

  // Handle duration input change
  const handleDurationChange = (inputValue: string) => {
    setDisplayValue(inputValue);

    if (inputValue !== '' && timeUnitId) {
      const unitId = Number(timeUnitId);
      const numericValue = Number(inputValue);
      const minutes = convertUnitToMinutes(numericValue, unitId);
      setDuration(minutes.toString());
    } else {
      setDuration(null);
    }
  };

  const durationErrorMessage = validationErrors.getError('config.durationInMinutes')?.message;
  const timeOfDayErrorMessage = validationErrors.getError('config.timeOfDayToSend')?.message;

  return (
    <>
      <SummarySidebarSection>
        <FormSection>
          <Input
            automationId='workflow-time-delay-input'
            disabled={builderState.isReadOnly}
            endAdornment={
              <SelectAdornment
                automationId='workflow-time-delay-unit-select'
                disabled={builderState.isReadOnly}
                disableNoneOption
                options={timeUnitOptions}
                useDefaultSelectRenderValue
                value={timeUnitId ? String(timeUnitId) : ''}
                onChange={handleUnitChange}
              />
            }
            errorMessage={durationErrorMessage}
            label='Delay Duration:'
            labelPlacement='top'
            placeholder='Enter delay duration'
            required
            type='number'
            value={displayValue}
            onChange={({ target }) => handleDurationChange(enforceWholeNumber(target.value))}
          />
          <TimeSelect
            automationId='workflow-time-delay-time-of-day-select'
            disabled={builderState.isReadOnly}
            errorMessage={timeOfDayErrorMessage}
            hoursOnly
            label='Time of day to resume:'
            labelPlacement='top'
            nullOptionLabel='No time preference'
            placeholder='Select a time'
            selectedValue={timeOfDayToSend}
            onChange={(time) => setTimeOfDayToSend(time)}
          />
        </FormSection>
      </SummarySidebarSection>
    </>
  );
}
